import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import ThreeCardJCardsOverlay from "./ThreeCardJCardsOverlay";
import ThreeCardJBetTable from "./ThreeCardJBetTable";
import RRefResults from "../andarBahar34/RRefResults";
import ThreeCardJResultBody from "../../casinoComponents/refResultModal/ThreeCardJResultBody";
import "../../../styles/games/threecardj.css";

// Reference range values (v-kvalmin / v-kvalmax): 100 → "100", 200000 → "2L"
const formatRange = (val) => {
  const n = Number(val);
  if (val == null || val === "" || Number.isNaN(n)) return "";
  if (n >= 100000) return `${parseFloat((n / 100000).toFixed(2))}L`;
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(2))}K`;
  return String(parseFloat(n.toFixed(2)));
};

export default function ThreeCardJ() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname);
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;

  useEffect(() => {
    const gameName = extractCasinoGame(pathname);
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("API Fetch Error:", err.message);
        setGameData(null); // clear data on error
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const subData = gameData?.data?.data?.sub || [];
  const matchId = gameData?.data?.data?.mid?.toString();

  const handleBetClick = (value) => {
    if (!value) return;
    fetchExposure();
    fetchMyBetsData();
  };

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          // Bets are stored per pick ("Yes 479", "No 2JK"), never as the runner "Yes" / "No":
          // the side's cell is the sum of its picks' worst cases.
          ["Yes", "No"].forEach((side) => {
            const sum = response.data.reduce(
              (acc, item) => (String(item.team_name || "").startsWith(`${side} `) ? acc + (parseFloat(item.exposure_amount) || 0) : acc),
              0
            );
            if (sum) exposureMap[side] = Math.round(sum * 100) / 100;
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  // My Bets: same API as lord-admin – GET /lords/bet-list/casino/open-downline?gameName=...
  const fetchMyBetsData = useCallback(async () => {
    const gameName = extractCasinoGame(pathname);
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname]);

  // Fetch exposure and My Bets on mount and periodically (My Bets every 5s like lord-admin)
  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => {
        fetchExposure();
      }, 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    fetchMyBetsData();
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMyBetsData]);

  const feed = gameData?.data?.data ?? {};
  const mid = feed?.mid || gameData?.data?.mid || "";
  const title = "3 Cards Judgement";
  // Reference mainData: feed sub with subtype = game type, ordered by sr
  const markets = (Array.isArray(subData) ? subData : [])
    .filter((m) => m?.subtype === gameName)
    .sort((x, y) => (Number(x?.sr) || 0) - (Number(y?.sr) || 0));

  // Reference page (casino-table.threecardj.detail-page-container): game header, video with the
  // 3-card overlay, then (once the feed has a round) the Yes / No card boxes, range, remark, Last Result.
  return (
    <CasinoTableLayout
      tableClass="threecardj detail-page-container"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={mid} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={feed?.card != null ? <ThreeCardJCardsOverlay cardString={feed.card} /> : null}
        />{" "}
        {mid ? (
          <div className="casino-detail">
            <ThreeCardJBetTable
              markets={markets}
              remark={feed?.remark}
              exposures={exposures}
              onBetClick={handleBetClick}
            />{" "}
            <div className="text-right casino-min-max">
              R:<span>{formatRange(markets[0]?.min)}</span>-<span>{formatRange(markets[0]?.max)}</span>
            </div>{" "}
            {feed?.remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{feed.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              gameData={gameData}
              resultComponent={
                <RRefResults
                  gameType={gameName}
                  mid={mid}
                  fallbackTitle={title}
                  renderBody={(t1) => <ThreeCardJResultBody t1={t1} />}
                />
              }
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
